import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState, useCallback } from "react";

import { message, Radio, Select, Spin, Table, Tour, Transfer } from "antd";
import { AiOutlineCloseCircle, AiOutlineSearch } from "react-icons/ai";
import { FaExclamationTriangle } from "react-icons/fa";

import './styles.scss'
import uris from "../../../config/uris";
import { antIcon } from "../../custom-playbook/manage-playbook";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import { FetchCompaniesAction } from "../../../store/slices/ai-chat/fetchCompanies";
import { RetrievePortfolioData } from "../../../store/slices/portfolio/retrievePortfolio";
import { CreatePortfolio, createPortfolioReset } from "../../../store/slices/portfolio/createPortfolio";

const columns = [
    {
        dataIndex: 'country',
        title: 'Country',
        width: "30%"
    },
    {
        dataIndex: 'companyName',
        title: 'Company Name',
        width: "80%"
    }
];

const TableFormattedData = React.memo((props) => {
    const { leftColumn, rightColumn, handleCompanySearch, setShowEntitiesAlert, showEntitiesAlert, entitiesMessage, searchCompanyInput, setSearchCompanyInput, ...remainingProps } = props;

    return (
        <Transfer {...remainingProps} locale={{ searchPlaceholder: "Search companies" }}>
            {({
                direction,
                filteredItems,
                onItemSelect,
                onItemSelectAll,
                selectedKeys: listSelectedKeys,
            }) => {
                const columns = direction === 'left' ? leftColumn : rightColumn;
                const rowSelection = {
                    onChange(selectedRowKeys) {
                        onItemSelectAll(selectedRowKeys, 'replace');
                    },
                    selectedRowKeys: listSelectedKeys,
                };
                const headerCount =
                    direction === "left"
                        ? `${listSelectedKeys.length}`
                        : `${filteredItems.length}`;
                return (
                    <div className="transfer-table">
                        {direction === "left" && (
                            <>
                                <div className="custom-searchbar">
                                    <span className="search-icon">
                                        <AiOutlineSearch />
                                    </span>
                                    <span className="search-text">
                                        <input
                                            placeholder="Type 3 or more letters to search companies"
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setSearchCompanyInput(value);
                                                handleCompanySearch("left", value);
                                            }}
                                            className="searchbar"
                                            value={searchCompanyInput}
                                        />
                                    </span>
                                    {searchCompanyInput && (
                                        <span className="clear-icon" onClick={() => {
                                            setSearchCompanyInput("");
                                            handleCompanySearch("left", "")
                                        }}>
                                            <AiOutlineCloseCircle />
                                        </span>
                                    )}
                                </div>
                                {showEntitiesAlert && (
                                    <div className="entities-alert">
                                        <div className="alert-header"><FaExclamationTriangle className="alert-icon" />Alert!</div>
                                        <div className="alert-message">
                                            {entitiesMessage}
                                        </div>
                                        <div className="alert-button-section">
                                            <div className="alert-button" onClick={() => setShowEntitiesAlert(false)}>Ok</div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        <div className="table-header">
                            {direction === "left" ? (
                                <>{headerCount} Items selected</>
                            ) : (
                                <>{headerCount} Items in portfolio</>
                            )}
                        </div>
                        <div className="table-component">
                            <Table
                                rowSelection={rowSelection}
                                columns={columns}
                                dataSource={filteredItems}
                                size="small"
                                pagination={false}
                                scroll={{ y: 363 }}
                                onRow={({ key }) => ({
                                    onClick: () => {
                                        onItemSelect(key, !listSelectedKeys.includes(key));
                                    },
                                })}
                            />
                        </div>
                    </div>
                );
            }}
        </Transfer>
    );
});

const Portfolio = ({ showBreadCrumbs = true }) => {
    const [showFilters, setShowFilters] = useState(true);
    const [countriesOptions, setCountriesOptions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState([]);
    const [companiesList, setCompaniesList] = useState([]);
    const [searchCompany, setSearchCompany] = useState("");
    const [selectedCompaniesList, setSelectedCompaniesList] = useState([]);
    const [searchCompanyInput, setSearchCompanyInput] = useState("");
    const [showEntitiesAlert, setShowEntitiesAlert] = useState(false);
    const [disclosure, selectedDisclosure] = useState("No");
    const [frequency, setFrequency] = useState("");
    const debounceTimeout = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { countries: countriesList, entities: companyList, filteredEntitiesCount: entitiesCount, message: entitiesMessage } = useSelector((state) => state?.fetchCompanies.data);
    const { isLoading: companiesLoading } = useSelector((state) => state?.fetchCompanies);
    const { data: portfolioData, success: portfolioCreateSuccess, error: portfolioCreateError, isLoading: portfolioCreateLoading } = useSelector((state) => state?.createPortfolio) || {};
    const { entities: userPortfolioData, defaultPortfolioId: portfolioId } = useSelector((state) => state?.retrievePortfolio?.data) || {};
    const { data: disclosureData } = useSelector((state) => state?.retrievePortfolio);

    const filterOption = (inputValue, option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    const handleChange = (newTargetKeys) => {
        setSelectedCompaniesList(newTargetKeys);
    };

    const handleCountryChange = (e) => {
        setSelectedCountry(e);
    };

    const handleFrequencyChange = (e) => {
        setFrequency(e?.target?.value);
    };

    const handleCompanySearch = useCallback((dir, value) => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        if (value?.length > 2 && dir === "left") {
            debounceTimeout.current = setTimeout(() => {
                setSearchCompany(value);
            }, 500);
        }
        else if (value === "") {
            setSearchCompany("");
        }
    }, []);

    const handleCreatePortfolio = () => {
        if (portfolioCreateLoading) return;
        if (selectedCompaniesList?.length === 0) {
            message.error("Please select at least one company to add to your portfolio");
            return;
        }
        const payload = {
            portfolioName: "Default Portfolio",
            entitiesList: selectedCompaniesList,
            isEdit: userPortfolioData?.length > 0 ? true : false,
            ...(userPortfolioData?.length > 0 && { portfolioId: portfolioId }),
            isNotifyRequired: (disclosure === "Yes") ? true : false,
            ...(disclosure === "Yes" ? { notifyType: frequency } : {})
        };
        dispatch(CreatePortfolio(payload));
    }

    const handleCancel = () => {
        setSelectedCompaniesList([]);
        setSearchCompany("");
        setSearchCompanyInput("");
        // setSelectedCountry(defaultOption?.label);
    }

    const handleDisclosureChange = (e) => {
        selectedDisclosure(e?.target?.value)
    }

    useEffect(() => {
        if (countriesList?.length > 0) {
            setCountriesOptions(countriesList);
        }
    }, [countriesList]);

    useEffect(() => {
        if (portfolioCreateSuccess) {
            message.success(portfolioData?.message);
            dispatch(createPortfolioReset());
            navigate("/company/research");
        } else if (portfolioCreateError) {
            message.error(portfolioCreateError?.message);
            dispatch(createPortfolioReset());
        }
    }, [portfolioCreateSuccess, portfolioCreateError, portfolioData]);

    useEffect(() => {
        if (companyList || userPortfolioData) {
            const formattedCompanies = (companyList || []).map(item => ({
                key: item?.value,
                label: item?.label,
                companyName: item?.label,
                country: item?.country,
            }));

            const formattedPortfolio = (userPortfolioData || []).map(item => ({
                key: item?.value,
                label: item?.label,
                companyName: item?.label,
                country: item?.country,
            }));

            const preservedSelectedCompanies = (selectedCompaniesList || []).map(key => {
                return (
                    formattedCompanies.find(item => item.key === key) ||
                    formattedPortfolio.find(item => item.key === key) ||
                    companiesList.find(item => item.key === key) ||
                    { key, label: key, companyName: key, country: "" }
                );
            });

            const mergedData = [
                ...formattedCompanies,
                ...formattedPortfolio.filter(
                    portfolioItem => !formattedCompanies.some(companyItem => companyItem.key === portfolioItem.key)
                ),
                ...preservedSelectedCompanies.filter(
                    selectedItem =>
                        !formattedCompanies.some(companyItem => companyItem.key === selectedItem.key) &&
                        !formattedPortfolio.some(portfolioItem => portfolioItem.key === selectedItem.key)
                ),
            ];

            setCompaniesList(mergedData);
        }
    }, [companyList, userPortfolioData]);

    useEffect(() => {
        if (!selectedCountry.includes("All") && selectedCountry?.length > 0 && entitiesCount > 1000 && companyList?.length > 1) {
            setShowEntitiesAlert(true);
        } else {
            setShowEntitiesAlert(false);
        }
    }, [selectedCountry, entitiesCount, companyList])

    useEffect(() => {
        const formattedPortfolio = (userPortfolioData || []).map(item => ({
            key: item?.value,
            label: item?.label,
            companyName: item?.label,
            country: item?.country,
        }));
        if (userPortfolioData?.length > 0 && selectedCompaniesList.length === 0) {
            setSelectedCompaniesList(formattedPortfolio.map(item => item.key));
        }
    }, [userPortfolioData])

    useEffect(() => {
        const payload = {
            isUserPortfolio: true,
            "searchType": "Company",
            "searchBy": "Public",
            "country": selectedCountry ? selectedCountry : [],
            "searchKeyword": searchCompany || "",
            "companyName": []
        };
        dispatch(FetchCompaniesAction(payload));
    }, [selectedCountry, searchCompany]);

    useEffect(() => {
        const payload = {
            "searchType": "Company",
            "searchBy": "Public",
            "companyName": [],
            "country": []
        };
        dispatch(RetrievePortfolioData(payload));
    }, []);

    useEffect(() => {
        if (disclosureData) {
            if (disclosureData?.isNotifyRequired) {
                selectedDisclosure("Yes");
                setFrequency(disclosureData?.notifyType || "Weekly");
            } else {
                selectedDisclosure("No");
                setFrequency("");
            }
        }
    }, [disclosureData]);

    return (
        <div className="portfolio-main">
            {showBreadCrumbs && <Breadcrumbs />}
            <div className="portfolio-filters">
                {showFilters && (
                    <div className="show-filters-section">
                        <div className="portfolio-filters-section">
                            <div className="input-wrapper">
                                <div className="input-label">
                                    Country
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        mode="multiple"
                                        maxTagCount={2}
                                        className="filter-select"
                                        options={countriesOptions}
                                        onChange={handleCountryChange}
                                        value={selectedCountry}
                                        placeholder="Select Country"
                                        optionFilterProp="label"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>
                            <div className="disclosure-input-wrapper">
                                <div>
                                    <div className="disclosure-input-label">
                                        Need Disclosure Email Alert?
                                    </div>
                                    <div className="disclosure-input-container">
                                        <Radio.Group
                                            options={[
                                                { label: "Yes", value: "Yes" },
                                                { label: "No", value: "No" },
                                            ]}
                                            onChange={handleDisclosureChange}
                                            value={disclosure}
                                        />
                                    </div>
                                </div>
                                <div>
                                    {disclosure === "Yes" && <div>
                                        <div className="frequency-container">
                                            <div className="disclosure-input-label">
                                                Frequency
                                            </div>
                                            <div className="disclosure-input-container">
                                                <Radio.Group
                                                    options={[
                                                        // { label: "Daily", value: "Daily" },
                                                        { label: "Weekly", value: "Weekly" },
                                                        { label: "Monthly", value: "Monthly" },
                                                    ]}
                                                    onChange={handleFrequencyChange}
                                                    value={frequency}
                                                />
                                            </div>
                                        </div>
                                    </div>}
                                </div>
                            </div>

                        </div>
                    </div>
                )}
            </div>
            <div className="portfolio-content">
                <div className="portfolio-content-header">
                    <div className="content">Select companies to add to your portfolio</div>
                </div>
                <div className="portfolio-table">
                    {companiesLoading && <div className="loader"><Spin size="large" /></div>}
                    <TableFormattedData
                        dataSource={companiesList}
                        showSearch
                        filterOption={filterOption}
                        targetKeys={selectedCompaniesList}
                        searchValues={searchCompany}
                        onChange={handleChange}
                        handleCompanySearch={handleCompanySearch}
                        searchCompanyInput={searchCompanyInput}
                        setSearchCompanyInput={setSearchCompanyInput}
                        showEntitiesAlert={showEntitiesAlert}
                        entitiesMessage={entitiesMessage}
                        setShowEntitiesAlert={setShowEntitiesAlert}
                        render={item => item.label}
                        leftColumn={columns}
                        rightColumn={columns}
                        showSelectAll={false}
                    />
                </div>
            </div>
            <div className="portfolio-actions-container">
                <div className="portfolio-buttons">
                    <div className="button-wrapper save" onClick={handleCreatePortfolio}>
                        {portfolioCreateLoading ? <Spin indicator={antIcon} /> : "Save"}
                    </div>
                    <div className="button-wrapper cancel" onClick={handleCancel}>Cancel</div>
                </div>
            </div>
        </div>
    );
};

export default Portfolio;